import React, { useEffect, useMemo, useState } from 'react';

import FormModal from 'components/FormModal';
import TextField from 'components/DesignSystem/TextField';
import Button from 'components/DesignSystem/Button';
import Selector from 'components/DesignSystem/Selector';
import RadioButton from 'components/DesignSystem/RadioButton';
import AlertsCollection, { CustomAlert } from 'components/AlertsCollection';
import useGroupForm from 'hooks/LocationForm/RegisterForm/useGroupForm';
import useLocationForm from 'hooks/LocationForm/RegisterForm/useLocationForm';
import { Form, Spinner } from 'react-bootstrap';

import './index.scss';

import type { GetAgroLocationGroups } from 'types/Meteorology/Agro/getAgroLocations';
import Snackbar from 'components/DesignSystem/Snackbar';

export default function RegisterLocationForm({
  show,
  onHide,
  alert,
  groups,
  saveGroup,
  saveLocationParams,
  cities,
  useCoordinates = true,
  usePredef = true,
  useShape = false,
}: {
  show: boolean;
  onHide: () => void;
  alert: CustomAlert | undefined;
  groups: GetAgroLocationGroups[] | undefined;
  saveGroup: any;
  saveLocationParams: any;
  cities: any;
  useCoordinates?: boolean;
  usePredef?: boolean;
  useShape?: boolean;
}) {
  const [entity, setEntity] = useState('');
  const [lockEntity, setLockEntity] = useState(false);
  const creatingGroup = entity === 'group';
  const creatingLocation = entity === 'location';
  const disableGroup = lockEntity && !creatingGroup;
  const disableLocation = lockEntity && !creatingLocation;

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccessful, setSaveSuccessful] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const {
    newGroup,
    setNewGroup,
    selectedGroup,
    setSelectedGroup,
    updateNewGroupField,
  } = useGroupForm();

  const {
    method,
    setMethod,
    city,
    setCity,
    changeNameField,
    changeLatField,
    changeLonField,
    isLatLonMethod,
    isPredefMethod,
    isShapeMethod,
    locationName,
    setLocationName,
    lat,
    setLat,
    lon,
    setLon,
  } = useLocationForm();

  const selectedGroupName = (groups && selectedGroup)
    ? groups.find((g) => g.id === selectedGroup)?.name
    : undefined;
  const formTitle = (creatingLocation && selectedGroupName)
    ? `Grupo: ${selectedGroupName}`
    : 'Novo cadastro';

  const onSaveGroup = () => saveGroup(newGroup, '');
  const onSaveLocation = (group) => saveLocationParams({
    group,
    locationName,
    method,
    lat,
    lon,
    city,
  });
  const onSave = () => {
    if (creatingGroup) {
      onSaveGroup();
    } else if (creatingLocation) {
      onSaveLocation(selectedGroup);
    }
    setIsSaving(true);
  };
  const saveDisabled = useMemo(() => {
    if (creatingGroup) return !(newGroup.length >= 3 && newGroup.length <= 30);
    if (creatingLocation) return !(selectedGroup && ((locationName && lat && lon) || city));
    return true;
  }, [creatingGroup, creatingLocation, newGroup, selectedGroup, locationName, lat, lon, city]);
  const saveLabel = useMemo(() => {
    if (creatingGroup) return 'Salvar grupo';
    if (creatingLocation) return 'Salvar local';
    return 'Salvar';
  }, [creatingGroup, creatingLocation]);

  const onFormForNewGroup = () => {
    setLockEntity(false);
    setIsSaving(false);
    setSaveSuccessful(false);
    setShowAlert(false);
    setNewGroup('');
    setMethod(undefined);
    setCity('');
    setLocationName('');
    setLat('');
    setLon('');
    setEntity('group');
  };
  const onFormForNewLocation = () => {
    setLockEntity(true);
    setIsSaving(false);
    setSaveSuccessful(false);
    setShowAlert(false);
    setCity('');
    setLocationName('');
    setLat('');
    setLon('');
    setEntity('location');
  };

  const cleanupHide = () => {
    setEntity('');
    setLockEntity(false);
    setIsSaving(false);
    setSaveSuccessful(false);
    setShowAlert(false);
    setNewGroup('');
    setSelectedGroup(undefined);
    setMethod(undefined);
    setCity('');
    setLocationName('');
    setLat('');
    setLon('');
    onHide();
  };

  useEffect(() => {
    const newlyAddedGroup = groups?.find((g) => g.name === newGroup);
    if (newlyAddedGroup) setSelectedGroup(newlyAddedGroup.id);
  }, [groups, newGroup, setSelectedGroup]);

  useEffect(() => {
    if (alert) {
      if (alert.variant === 'success') {
        setSaveSuccessful(true);
        setIsSaving(false);
      } else {
        setShowAlert(true);
        setIsSaving(false);
      }
    }
  }, [alert]);

  if (saveSuccessful && creatingGroup) {
    return (
      <FormModal show={show} onHide={cleanupHide} title="Cadastro realizado com sucesso" lastAsFooter dialogClassName="modal-dialog-centered">
        <div className="d-flex gap-2">
          <Button onClick={onFormForNewLocation} className="flex-grow-1">Cadastrar novo local para este grupo</Button>
          <Button onClick={onFormForNewGroup} className="flex-grow-1">Cadastrar novo grupo</Button>
        </div>
        <Button onClick={cleanupHide}>Fechar</Button>
      </FormModal>
    );
  }

  if (saveSuccessful && creatingLocation) {
    return (
      <FormModal show={show} onHide={cleanupHide} title="Cadastro realizado com sucesso" lastAsFooter dialogClassName="modal-dialog-centered">
        <Snackbar variant="danger" className="mb-3">
          Os dados serão processados para esse novo local e estarão disponíveis a partir das 18h
          do dia seguinte.
        </Snackbar>
        <div className="d-flex gap-2">
          <Button onClick={onFormForNewLocation} className="flex-grow-1">Cadastrar novo local</Button>
          <Button onClick={onFormForNewGroup} className="flex-grow-1">Cadastrar novo grupo</Button>
        </div>
        <Button onClick={cleanupHide}>Fechar</Button>
      </FormModal>
    );
  }

  return (
    <FormModal show={show} onHide={cleanupHide} title={formTitle} lastAsFooter dialogClassName="modal-dialog-centered">
      {showAlert && alert && (
        <div className="mb-4">
          <AlertsCollection alerts={[alert]} />
        </div>
      )}
      <div className="location-register-container d-flex flex-column gap-4">
        <div>
          <RadioButton label="Grupo" checked={entity === 'group'} onChange={() => setEntity('group')} disabled={disableGroup} />
          <RadioButton label="Local" checked={entity === 'location'} onChange={() => setEntity('location')} disabled={disableLocation} />
        </div>
        {creatingGroup && (
          <div>
            <TextField
              label="Cadastro grupo"
              onChange={updateNewGroupField}
              value={newGroup}
              controlId="group-name"
            />
          </div>
        )}
        {creatingLocation && (
          <div className="d-flex flex-column gap-4">
            <div className="group-selector">
              <Selector
                label="Grupo"
                placeholder="Selecione o grupo para o novo local"
                value={selectedGroup}
                setValue={setSelectedGroup}
                search
                asBlock
              >
                {groups?.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Selector>
            </div>
            {selectedGroup && (
              <>
                <Selector
                  label="Local"
                  placeholder="Selecione uma opção"
                  value={method}
                  setValue={setMethod}
                >
                  {useCoordinates
                    && (<option key="latlon" value="latlon">Latitude/Longitude</option>)}
                  {usePredef
                    && (<option key="predef" value="predef">Locais pré-definidos</option>)}
                  {useShape
                    && (<option key="shape" value="shape">Contorno (shape)</option>)}
                </Selector>
                {isLatLonMethod && (
                  <div className="d-flex flex-column gap-3">
                    <TextField
                      id="location-name"
                      label="Nome do Local"
                      onChange={changeNameField}
                      value={locationName}
                    />
                    <div className="d-flex justify-content-between gap-2">
                      <TextField
                        id="latitude"
                        label="Latitude"
                        value={lat}
                        onChange={changeLatField}
                        className="flex-grow-1"
                      />
                      <TextField
                        id="longitude"
                        label="Longitude"
                        value={lon}
                        onChange={changeLonField}
                        className="flex-grow-1"
                      />
                    </div>
                  </div>
                )}
                {isPredefMethod && (
                  <div className="city-selector">
                    <Selector
                      label="Cidade"
                      placeholder="Selecione uma cidade"
                      value={city}
                      setValue={setCity}
                      search
                      searchPlaceholder="Busca"
                      asBlock
                    >
                      {cities?.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </Selector>
                  </div>
                )}
                {isShapeMethod && (
                  <Form.Control
                    type="file"
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
      <div className="d-flex justify-content-end">
        <Button onClick={onSave} disabled={saveDisabled || isSaving}>
          {saveLabel}
          {isSaving && <Spinner size="sm" className="ms-2" />}
        </Button>
      </div>
    </FormModal>
  );
}
